import { BankOutlined, CheckOutlined } from '@ant-design/icons';
import {
  AdminPortal,
  ContextHolder,
  useAuth,
  useAuthActions,
  useFeatureEntitlements,
  useTenantsState,
  usePermissionEntitlements,
} from '@frontegg/react';
import { FronteggContext } from '@frontegg/rest-api';
import {
  Dropdown as AntDropdown,
  AutoComplete,
  Divider,
  Menu,
  Alert,
  Button,
  Breadcrumb,
} from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BackIconMobile,
  CallIcon,
  DropdownIcon,
  EditIcon,
  SearchIcon,
  SignoutIcon,
  ToggleIcon,
} from 'src/icons';
import { ProIcon } from 'src/icons/Pro.icon';
import { AxiosAmbient } from 'src/lib';
import { getFromStorage } from 'src/lib/storage';
import { StripeBillingCustomerPortal } from 'src/modules/ambient_solo/components/Payments/StripeCustomerPortal';
import { UpgradeModal } from 'src/modules/ambient_solo/components/Payments/UpgradeModal';
import {
  encounterListSlice,
  resetEncounterState,
  setMobileRecord,
} from 'src/modules/ambient_solo/store/slices/encounter';
import {
  getAllPractitionersNamesSlice,
  getAppointmentByFilterSlice,
  getAppointmentsByStatusSlice,
  getAppointmentUuidSlice,
  getConditionByAppointmentTypeSlice,
  getPractitionerPreferencesSlice,
  getPractitionersAndPatientsSlice,
} from 'src/store/slices/appointments.slice';
import { getSettingsNotificationsSlice } from 'src/store/slices/settings.slice';
import { useNavigationStore } from 'src/hooks/navigation';
import { Text } from '../baseComponents';
import { ProfileTopNavbar } from '../baseComponents/ProfileTopNavbar';
import { PLGUsersCard } from './PLGUsersCard';
import { ReclaimModal } from './Reclaim.Modal';
import { SupaDemo } from './supaDemo';
import moment from 'moment-timezone';
import { analytics, RUM } from 'src/services';
import { useIsOP } from 'src/hooks/misc';
import { CalendarOutlined } from '@ant-design/icons';
import { isOPUpgradeModalVisibleSlice } from 'src/modules/ambient_solo/store/slices/encounter';
import { useQueryClient } from '@tanstack/react-query';
import {
  resetSummaryState,
  summaryListSlice,
} from 'src/modules/ambient_solo/store/slices/summary';

const remainingDaysToNextMonth = 30 - new Date().getDate();

export const TopNavbar = ({ handleToggleSidebar, isMinimized }) => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isOP = useIsOP();
  let { tenants } = useTenantsState();
  const { switchTenant } = useAuthActions();

  const { currentPractitioner, currentPractitionerEntitlements } = useSelector(
    (state) => state?.practitionerState
  );
  const { quota, timeQuota } = useSelector((state) => state?.quotaState);
  const { isMobileRecord } = useSelector((state) => state?.encounterState);
  const { isEntitled: canCreateEncounter } =
    usePermissionEntitlements('encounter.create');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [OpenReclaim, setOpenReclaim] = useState(false);
  const [isUpgradeModalVisible, setIsUpgradeModalVisible] = useState(false);

  const encountersRemaining = quota?.encounters?.remaining;
  const timeBasedQuota = timeQuota?.time_based_quota;
  const timeBasedQuotaDaysRemaining = timeQuota?.remaining_days;

  const { isEntitled: templatesExchangeFeature } =
    useFeatureEntitlements('template-exchange');
  const { isEntitled: isBookDemoFeatureEntitled } =
    useFeatureEntitlements('book-demo');
  const { isEntitled: isUpgradePlanFeatureEntitled } =
    useFeatureEntitlements('upgrade-plan');
  const { isEntitled: isPaymentsFeatureEntitled } =
    useFeatureEntitlements('payments');
  const [isDemoEntitled, setIsDemoEntitled] = useState({
    loading: false,
    error: null,
  });

  const mobile_encounter_id = getFromStorage(
    `${currentPractitioner?.org_uuid}_topbar_encounter_id`
  );
  const switchOrganization = (e) => {
    localStorage.removeItem('filter_practitioner_id');
    localStorage.removeItem('selectedPractitioners');
    localStorage.removeItem('show_current_practitioner');
    localStorage.clear();

    // Clear important Redux state slices
    dispatch(getPractitionersAndPatientsSlice([]));
    dispatch(getAppointmentByFilterSlice([]));
    dispatch(getAppointmentsByStatusSlice([]));
    dispatch(getConditionByAppointmentTypeSlice(null));
    dispatch(getPractitionerPreferencesSlice(null));
    dispatch(getSettingsNotificationsSlice(null));
    dispatch(encounterListSlice([]));
    dispatch(summaryListSlice([]));
    dispatch(getAllPractitionersNamesSlice([]));
    dispatch(getAppointmentUuidSlice({}));
    dispatch(resetEncounterState());
    dispatch(resetSummaryState());

    const selectedId = e.key;
    switchTenant({ tenantId: selectedId });
  };

  const [pageTitle, setPageTitle] = useState('');

  const { title, breadcrumb } = useNavigationStore();

  const { user } = useAuth();

  if (user?.roles?.map((role) => role?.key)?.includes('provider')) {
    tenants = tenants.filter(
      (tenant) => tenant?.tenantId === currentPractitioner?.org_uuid
    );
  }

  const cachedUserName = getFromStorage(`user_name`);
  const org_id = currentPractitioner.org_id;
  const uuid = getFromStorage('uuid');

  const settings = () => {
    setDropdownVisible(false);
    // eslint-disable-next-line no-undef
    analytics.track('Viewed Settings', {
      email: user?.email,
      orgId: org_id,
    });

    // Clear browser cache before opening AdminPortal
    if ('caches' in window) {
      caches
        .keys()
        .then((cacheNames) => {
          return Promise.all(
            cacheNames.map((cacheName) => {
              return caches.delete(cacheName);
            })
          );
        })
        .then(() => {
          console.log('Browser cache cleared successfully');
          AdminPortal.show();
        })
        .catch((error) => {
          console.error('Error clearing cache:', error);
          AdminPortal.show();
        });
    } else {
      // Fallback if cache API is not available
      AdminPortal.show();
    }
  };

  const logout = () => {
    setDropdownVisible(false);
    const baseUrl = ContextHolder.getContext().baseUrl;
    // eslint-disable-next-line no-undef
    analytics.track('Signed Out', {
      email: user?.email,
      orgId: org_id,
    });
    RUM.clearUser();
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_id');
    localStorage.removeItem('show_current_practitioner');
    localStorage.clear();
    window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${
      isOP ? `${baseUrl}/oauth/account/login?organization=op` : window.location
    }`;
  };

  useEffect(() => {
    if (location.pathname !== '/settings') {
      dispatch(getPractitionerPreferencesSlice(null));
      dispatch(getConditionByAppointmentTypeSlice(null));
      dispatch(getSettingsNotificationsSlice(null));
    }
  }, [dispatch, location]);

  useEffect(() => {
    let titleObject = {
      [`/schedule/${uuid}`]: 'Patient history',
      '/appointments': 'Appointments',
      '/patients': 'Patient Search',
      '/current-encounters': 'Current Encounter',
      '/encounters': 'Encounters',
      '/encountersbin': 'Aura Bin',
      [`/mobileEncounterDetails/${mobile_encounter_id}`]: 'Encounters',
      '/encounter': 'Aura AI',
      '/setting': 'Customize',
      '/settings': 'Settings',
      '/superadmin': 'Super admin',
      '/csvupload': 'CSV Upload',
      '/superadmin/officepracticum': 'Office Practicum Settings',
      '/op-settings': 'Office Practicum Settings',
      '/open-dr-settings': 'OpenDr Settings',
      // '/humanscribe': 'Human Scribe',
      '/memory': 'Memory',
      '/templates': 'Templates',
      '/prompts': 'Prompts',
      '/ai': 'AI Configurations',
      '/formconditions': 'Form Conditions',
      '/templatesexchange': '',
      '/autotemplate': 'Auto Template',
      '/frontdesk/calls': 'Calls',
      '/agents': 'Agents',
    };
    setPageTitle(`${titleObject[location?.pathname] || ''} `);
  }, [location, uuid, isMobileRecord, mobile_encounter_id]);

  let fullName = currentPractitioner.practitioner_name;
  let nameWithoutTitle = fullName.replace('Dr ', '');

  const isMobileView = window.innerWidth < 1260;
  const appointmentUUID = getFromStorage(
    `${currentPractitioner?.org_uuid}_appointmentUUID`
  );

  const isEncountersPage = () => {
    return (
      (location.pathname !== '/appointments' &&
        location.pathname !== '/settings' &&
        location.pathname !== `/schedule/${appointmentUUID}`) ||
      location.pathname === `/mobileEncounterDetails/${mobile_encounter_id}`
    );
  };

  const nullSelectedRecord = () => {
    if (location.pathname === `/schedule/${appointmentUUID}`) {
      history.back();
    } else if (location.pathname === `/settings`) {
      navigate(`/appointments`);
    } else {
      navigate(`/encounters`);
    }
    dispatch(setMobileRecord(false));
  };

  const [filteredTenantsNames, setFilteredTenantsNames] = useState(tenants);
  const [searchText, setSearchText] = useState('');

  const handleSearch = (value) => {
    setSearchText(value);
    const filtered = tenants.filter((tenant) =>
      tenant.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredTenantsNames(filtered);
  };

  const resetDropdown = () => {
    setDropdownVisible(false);
    setSearchText('');
    setFilteredTenantsNames(tenants);
  };

  // useEffect(() => {
  //   if (location.pathname === '/templatesexchange') {
  //     setIsLoading(true);
  //     dispatch(getAllTemplatesExchangeListAction()).finally(() =>
  //       setIsLoading(false)
  //     );
  //   }
  // }, [dispatch, location.pathname]);

  useEffect(() => {
    const checkProTrialHistory = async () => {
      setIsDemoEntitled({ loading: true });
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };

      try {
        const result = await AxiosAmbient.get('/aura_pro_trial/entitlement', {
          headers,
        });
        if (result.status < 300) {
          setIsDemoEntitled({
            entitled: !!result.data.entitled,
            loading: false,
          });
        }
      } catch (error) {
        setIsDemoEntitled({ error, loading: false });
      }
    };
    checkProTrialHistory();
  }, []);

  const isAuraPro = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'pro'
  );

  const isAuraProTrial = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'pro' && plan?.metadata?.trial
  );

  /**
   * Top Nav CTA scenarios
   * -------------------------------------------
   * Non Pro PLG user => View demo |
   * Non Pro PLG user with expired trial => Upgrade
   * Pro trial user => View demo |
   * Top tier Pro user => Book a demo |
   */
  const auraProTrialPlanExpiry = currentPractitionerEntitlements?.plan_expiry;
  const tz = moment.tz.guess();
  const auraProTrialRemainingSeconds = auraProTrialPlanExpiry
    ? moment.tz(auraProTrialPlanExpiry, tz).diff(moment().tz(tz), 'seconds')
    : null;
  const isAuraProTrialExpired =
    isAuraProTrial && auraProTrialRemainingSeconds <= 0;
  const isAuraProTrialNotExpired =
    isAuraProTrial && auraProTrialRemainingSeconds > 0;

  const canViewDemo =
    isAuraProTrialNotExpired || (!isAuraPro && isDemoEntitled.entitled); // User is either pro trial or never been pro
  const canBookADemo =
    isBookDemoFeatureEntitled && isAuraPro && !isAuraProTrial; // only show book a demo for top tier pro users

  const canUpgradeToPro =
    isUpgradePlanFeatureEntitled &&
    (!isAuraPro || // User is free
      isAuraProTrial || // User is on pro trial
      isAuraProTrialExpired); // User's pro trial has expired

  const handleBookACall = () => {
    setOpenReclaim(true);
    // eslint-disable-next-line no-undef
    analytics.track('Clicked Book Demo From Top Menu', { email: user?.email });
  };

  const handleUpgradeToPro = () => {
    // eslint-disable-next-line no-undef
    analytics.track('Clicked Upgrade', { email: user?.email });
    setIsUpgradeModalVisible(true);
  };

  const gradientButtonClass =
    'text-white bg-gradient-to-br from-pink-500 to-blue-700 bg-origin-border bg-clip-padding flex items-center p-2 rounded-xl hover:shadow-xl font-medium px-2 lg:px-4 gap-2';

  const singleOrganisationAccessAccount =
    !searchText && filteredTenantsNames.length <= 1;
  // Hide organization switcher for single accounts
  const topNavbarDropdownOptions = [
    {
      label: (
        <div className="flex p-4">
          <EditIcon className={'mr-2'} />
          <span className="font-sans text-sm"> Settings</span>
        </div>
      ),
      key: 'settings',
      onClick: settings,
    },
    {
      label: (
        <>
          <Divider className="m-0 p-0" />
        </>
      ),
      className: 'p-0',
    },
    !singleOrganisationAccessAccount && {
      label: (
        <div className="flex p-4">
          <div className="text-sm">
            <BankOutlined className="mr-2" />
            Organizations
          </div>
        </div>
      ),
    },
    !singleOrganisationAccessAccount && {
      label: (
        <div className="p-2">
          <div className=" w-[19rem] ant-select-selector drop-shadow-md bg-white rounded-xl">
            <AutoComplete
              bordered={false}
              className="border-none font-sans h-10 flex items-center border-0 focus:outline-nones"
              style={{ WebkitTextFillColor: '#000' }}
              placeholder="Search organization"
              suffixIcon={<SearchIcon />}
              onSearch={handleSearch}
              onFocus={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              value={searchText}
              id="Searchorganization"
            />
          </div>
        </div>
      ),
    },
    !singleOrganisationAccessAccount && {
      label: (
        <Menu
          style={{
            maxHeight: '200px',
            overflowY: 'auto',
            backgroundColor: 'white',
          }}
          id="menuorganization"
          items={filteredTenantsNames.map((tenant) => ({
            label: (
              <div
                key={tenant.tenantId}
                className="flex items-center p-2 text-sm text-gray-600"
              >
                {tenant.name}
                {tenant.tenantId === user.tenantId && (
                  <CheckOutlined className="ml-4 w-3 h-3" />
                )}
              </div>
            ),
            key: tenant.tenantId,
            disabled: tenant.tenantId === user.tenantId,
            onClick: switchOrganization,
          }))}
        />
      ),
    },
    !singleOrganisationAccessAccount &&
      currentPractitioner?.stripe_customer_id && {
        label: <Divider className="m-0 p-0" />,
      },
    currentPractitioner?.stripe_customer_id &&
      isPaymentsFeatureEntitled && {
        key: 'billing',
        label: <StripeBillingCustomerPortal />,
      },
    currentPractitioner?.stripe_customer_id && {
      label: <Divider className="m-0 p-0" />,
    },
    {
      label: (
        <div className="flex p-4">
          <SignoutIcon className={'mr-2'} />
          <span className="font-sans text-sm">Sign out</span>
        </div>
      ),
      key: 'logout',
      onClick: logout,
    },
  ].filter(Boolean);

  const renderPageHeader = () => {
    let titleJsx = null;
    let breadcrumbJsx = null;

    if (title) {
      if (typeof title === 'string') {
        titleJsx = <div className="font-semibold text-lg">{title}</div>;
      } else {
        titleJsx = title;
      }
    }

    if (breadcrumb?.length) {
      breadcrumbJsx = (
        <Breadcrumb
          items={breadcrumb.map(({ onClick, href, icon, title }) => ({
            title: (
              <div
                className={[
                  'flex items-center gap-1 h-[22px]',
                  (onClick || href) &&
                    'cursor-pointer text-primary hover:underline',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  onClick?.();
                  if (href) {
                    navigate(href);
                  }
                }}
              >
                {icon}
                {title}
              </div>
            ),
          }))}
        />
      );
    }

    if (pageTitle && !titleJsx && !breadcrumbJsx) {
      if (typeof pageTitle === 'string') {
        titleJsx = <div className="font-semibold text-lg">{pageTitle}</div>;
      } else {
        titleJsx = pageTitle;
      }
    }
    return titleJsx || breadcrumbJsx ? (
      <div className="flex flex-col">
        {breadcrumbJsx}
        {titleJsx}
      </div>
    ) : null;
  };

  return (
    <nav
      className={`top-navbar sticky top-0 left-0 z-[98] flex justify-between items-center ${
        isMinimized ? 'top-navbar--minimized' : ''
      }`}
    >
      {location.pathname === '/templatesexchange' &&
        !templatesExchangeFeature && <PLGUsersCard />}
      <div className="top-navbar-left flex items-center justify-start">
        {!isMobileView && (
          <ToggleIcon
            className={'xl:hidden mr-3 sm:mr-4'}
            onClick={handleToggleSidebar}
          />
        )}
        {(isMobileView && isMobileRecord && isEncountersPage()) ||
        (isMobileView &&
          (location.pathname === '/settings' ||
            location.pathname === '/setting')) ||
        location.pathname === `/schedule/${appointmentUUID}` ? (
          <div className="xs:pr-2 md:pr-5 pr-5">
            <BackIconMobile onClick={nullSelectedRecord} />
          </div>
        ) : (
          ''
        )}
        {renderPageHeader()}
      </div>
      <div
        className={`flex justify-end items-center ${
          isMobileView ? 'gap-2' : 'gap-5'
        }`}
      >
        {location.pathname === '/encounters' &&
          (typeof timeBasedQuotaDaysRemaining === 'number' ||
            typeof encountersRemaining === 'number') &&
          canCreateEncounter && (
            <Alert
              message={
                <>
                  <div className="flex flex-row justify-between">
                    <div>
                      <span className="text-sm">
                        {typeof timeBasedQuotaDaysRemaining === 'number' ? (
                          timeBasedQuotaDaysRemaining === 0 ? (
                            'Trial expired'
                          ) : (
                            `${Math.max(0, timeBasedQuotaDaysRemaining)}${
                              !isMobileView ? ' days of free trial left' : ''
                            }`
                          )
                        ) : typeof encountersRemaining === 'number' ? (
                          <>
                            {Math.max(0, encountersRemaining - 1)}{' '}
                            {!isMobileView && 'encounters left'}
                          </>
                        ) : (
                          ''
                        )}
                      </span>
                      {typeof encountersRemaining === 'number' &&
                        encountersRemaining <= 0 &&
                        !isMobileView && (
                          <div
                            className="text-gray-500"
                            style={{ fontSize: '10px' }}
                          >
                            <CalendarOutlined className="mr-0.5 text-gray-500" />{' '}
                            Free encounters in {remainingDaysToNextMonth} days
                          </div>
                        )}
                    </div>
                    {((typeof timeBasedQuotaDaysRemaining === 'number' &&
                      timeBasedQuotaDaysRemaining <= 0) ||
                      (typeof encountersRemaining === 'number' &&
                        encountersRemaining <= 0)) && (
                      <div
                        className={`flex items-center ${
                          isMobileView ? '' : 'ml-4'
                        }`}
                      >
                        <Button
                          type="default"
                          size="small"
                          className="!bg-[#faad14] !text-white !border-[#faad14] hover:!bg-[#eda017] hover:!border-[#eda017]"
                          onClick={() => {
                            dispatch(isOPUpgradeModalVisibleSlice(true));
                          }}
                        >
                          <span className="text-sm">Upgrade</span>
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              }
              type={
                (typeof timeBasedQuotaDaysRemaining === 'number' &&
                  timeBasedQuotaDaysRemaining > 0) ||
                (typeof encountersRemaining === 'number' &&
                  encountersRemaining > 0)
                  ? 'info'
                  : 'warning'
              }
              showIcon
            />
          )}
        {!(isDemoEntitled.loading || isDemoEntitled.error) && (
          <div className="flex items-center gap-3">
            {canViewDemo && (
              <SupaDemo
                className={gradientButtonClass}
                isMobileView={isMobileView}
              />
            )}
            {canUpgradeToPro ? (
              <button
                className={gradientButtonClass}
                type="button"
                onClick={handleUpgradeToPro}
              >
                <ProIcon fill="#fff" forceFlashIcon={true} />
                {isMobileView ? null : 'Upgrade'}
              </button>
            ) : null}
            {canBookADemo ? (
              <button
                className={gradientButtonClass}
                type="button"
                onClick={handleBookACall}
              >
                <CallIcon fill="#fff" />
                {isMobileView ? null : 'Book a demo'}
              </button>
            ) : null}

            <ReclaimModal
              OpenReclaim={OpenReclaim}
              setOpenReclaim={setOpenReclaim}
            />
            <UpgradeModal
              isVisible={isUpgradeModalVisible}
              setIsVisible={setIsUpgradeModalVisible}
            />
          </div>
        )}
        <div className="top-navbar-right flex items-center">
          <AntDropdown
            className="profile-dropdown"
            overlayClassName="profile-dropdown-overlay"
            open={dropdownVisible}
            onClick={() => setDropdownVisible(true)}
            onOpenChange={() => {
              resetDropdown();
            }}
            menu={{
              onClick: (e) => {
                if (e.domEvent.stopImmediatePropagation) {
                  e.domEvent.stopImmediatePropagation();
                }
                if (e.domEvent.stopPropagation) {
                  e.domEvent.stopPropagation();
                }
                if (e.domEvent.preventDefault) {
                  e.domEvent.preventDefault();
                }
              },
              items: topNavbarDropdownOptions,
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <div
              className="ant-dropdown-link"
              onClick={(e) => e.preventDefault()}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div className="profile-wrapper flex items-center cursor-pointer gap-1 sm:gap-2">
                <ProfileTopNavbar
                  name={nameWithoutTitle}
                  className={'profile-popover-logo'}
                />
                <Text type={'span'} className="user-name hidden sm:block">
                  {cachedUserName}
                  {tenants.length > 1 && (
                    <div className="text-xs text-right text-gray-500 flex items-center gap-1">
                      <BankOutlined />
                      {
                        tenants.find(
                          (tenant) => tenant.tenantId === user.tenantId
                        )?.name
                      }
                    </div>
                  )}
                </Text>
                <DropdownIcon />
              </div>
            </div>
          </AntDropdown>
        </div>
      </div>
    </nav>
  );
};
